import { yupResolver } from '@hookform/resolvers/yup'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import Loader from '../../../common/components/Loader'
import { DefaultImagesPayload } from '../../../common/models'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getDefaultImages,
  updateDefaultImages,
} from '../../../core/services/festival-settings'
import { uploadImage } from '../../../core/services/upload-image'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Header, PageTitle } from '@/components'
import { Button, Card, Input } from '@/components/ui'
import { AlertRatio } from '@/components/AlertRatio'

interface IFormValues {
  socialShareImage?: string
  heroImage?: string
  artistImage?: string
  newsImage?: string
  videoGalleryImage?: string
  infoImage?: string
  stagesLogo?: string
  checkinImage?: string
}

interface IImageValues {
  socialShareImage?: string
  socialShareImageName?: string
  heroImage?: string
  heroImageName?: string
  artistImage?: string
  artistImageName?: string
  newsImage?: string
  newsImageName?: string
  videoGalleryImage?: string
  videoGalleryImageName?: string
  infoImage?: string
  infoImageName?: string
  stagesLogo?: string
  stagesLogoName?: string
  checkinImage?: string
  checkinImageName?: string
}

export const DefaultImagesPage: React.FC = () => {
  const navigate = useNavigate()
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name
  const [invalidPhoto, setInvalidPhoto] = useState<boolean[]>([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ])
  const [loading, setLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  const [defaultImagesState, setDefaultImagesState] = useState<IImageValues>({})

  const [isError, setIsError] = useState<boolean>(false)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  const getNameFromUrl = (imageUrl: string) => {
    if (!imageUrl) return ''
    const url = new URL(imageUrl)
    const i = url?.pathname?.lastIndexOf('/') + 1
    const imageName = url?.pathname?.substring(i)
    return imageName
  }

  const schema = yup.object().shape({
    socialShareImage: yup.string().optional(),
    heroImage: yup.string().optional(),
    artistImage: yup.string().optional(),
    newsImage: yup.string().optional(),
    videoGalleryImage: yup.string().optional(),
    infoImage: yup.string().optional(),
    stagesLogo: yup.string().optional(),
    checkinImage: yup.string().optional(),
  })

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver<IFormValues>(schema),
    defaultValues: {
      socialShareImage: '',
      heroImage: '',
      artistImage: '',
      newsImage: '',
      videoGalleryImage: '',
      infoImage: '',
      stagesLogo: '',
      checkinImage: '',
    },
  })

  const getData = useCallback(async () => {
    setLoading(true)
    try {
      const { data, status } = await getDefaultImages()
      if (status === 200) {
        setDefaultImagesState((s) => ({
          ...s,
          socialShareImage: data.socialShareImageLink,
          socialShareImageName: getNameFromUrl(data.socialShareImageLink),
          heroImage: data.heroImageLink,
          heroImageName: getNameFromUrl(data.heroImageLink),
          artistImage: data.artistImageLink,
          artistImageName: getNameFromUrl(data.artistImageLink),
          newsImage: data.newsImageLink,
          newsImageName: getNameFromUrl(data.newsImageLink),
          videoGalleryImage: data.videoGalleryImageLink,
          videoGalleryImageName: getNameFromUrl(data.videoGalleryImageLink),
          infoImage: data.infoImageLink,
          infoImageName: getNameFromUrl(data.infoImageLink),
          stagesLogo: data.stageImageLink,
          stagesLogoName: getNameFromUrl(data.stageImageLink),
          checkinImage: data.checkinImageLink,
          checkinImageName: getNameFromUrl(data.checkinImageLink),
        }))
        if (data.socialShareImageLink) {
          setValue('socialShareImage', data.socialShareImageLink)
        }

        if (data.heroImageLink) {
          setValue('heroImage', data.heroImageLink)
        }

        if (data.artistImageLink) {
          setValue('artistImage', data.artistImageLink)
        }

        if (data.newsImageLink) {
          setValue('newsImage', data.newsImageLink)
        }

        if (data.videoGalleryImageLink) {
          setValue('videoGalleryImage', data.videoGalleryImageLink)
        }

        if (data.infoImageLink) {
          setValue('infoImage', data.infoImageLink)
        }

        if (data.stageImageLink) {
          setValue('stagesLogo', data.stageImageLink)
        }

        if (data.checkinImageLink) {
          setValue('checkinImage', data.checkinImageLink)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [editionId, festivalName])

  useEffect(() => {
    getData()
  }, [getData, editionId, festivalName])

  const submit = async (values: IFormValues) => {
    setLoading(true)
    try {
      const payload: DefaultImagesPayload = {}

      if (values.artistImage) {
        payload.artistImageKey = values.artistImage
      }
      if (values.heroImage) {
        payload.heroImageKey = values.heroImage
      }
      if (values.infoImage) {
        payload.infoImageKey = values.infoImage
      }
      if (values.newsImage) {
        payload.newsImageKey = values.newsImage
      }
      if (values.socialShareImage) {
        payload.socialShareImageKey = values.socialShareImage
      }
      if (values.videoGalleryImage) {
        payload.videoGalleryImageKey = values.videoGalleryImage
      }
      if (values.stagesLogo) {
        payload.stageImageKey = values.stagesLogo
      }
      if (values.checkinImage) {
        payload.stageImageKey = values.checkinImage
      }

      const { data, status } = await updateDefaultImages(payload)

      if (status === 200) {
        setDefaultImagesState((s) => ({
          ...s,
          socialShareImage: data.socialShareImageLink,
          heroImage: data.heroImageLink,
          artistImage: data.artistImageLink,
          newsImage: data.newsImageLink,
          videoGalleryImage: data.videoGalleryImageLink,
          infoImage: data.infoImageLink,
          stagesLogo: data.stageImageLink,
          checkinImage: data.checkinImageLink,
        }))
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Header pageTitle="Default Images" />
      {loading && <Loader />}
      <Card className="p-4 max-h-[calc(100vh-150px)] overflow-y-auto">
        <PageTitle title="Default Images" />
        <div className="rounded-3xl border border-monochrome-120 p-4 mt-4">
          <form
            id="settings-default-images-form"
            onSubmit={handleSubmit(submit)}
          >
            <div className="flex flex-col gap-3">
              <div>
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      accept="image/*"
                      disabled={!hasEditPermission}
                      isInvalid={
                        !!errors?.socialShareImage?.message || invalidPhoto[0]
                      }
                      name="socialShareImage"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          if (file.size > 1_000_000) {
                            const temp = invalidPhoto
                            temp[0] = true
                            setInvalidPhoto(temp)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const { photoUrl, photoKey } = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            'DEFAULT_IMAGE_SOCIAL_SHARE',
                            false,
                          )
                          if (photoUrl && photoKey && file.name) {
                            setValue('socialShareImage', photoKey as string, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                            setDefaultImagesState((s) => ({
                              ...s,
                              socialShareImage: photoUrl,
                              socialShareImageName: file.name,
                            }))
                          } else {
                            const temp = invalidPhoto
                            temp[0] = true
                            setInvalidPhoto(temp)
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  imageName={defaultImagesState.socialShareImageName}
                  isUploading={isUploading}
                  url={defaultImagesState.socialShareImage}
                  onRemove={() =>
                    setDefaultImagesState((s) => ({
                      ...s,
                      socialShareImage: '',
                      socialShareImageName: '',
                    }))
                  }
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                  label="Default Social Share Image (1200X600)"
                  previewLabel="Default Social Share Image (1200X600)"
                />
                {isErrorRatio && invalidPhoto[0] && <AlertRatio />}
              </div>
              <div>
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      accept="image/*"
                      disabled={!hasEditPermission}
                      isInvalid={
                        !!errors?.heroImage?.message || invalidPhoto[1]
                      }
                      name="heroImage"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          if (file.size > 1_000_000) {
                            const temp = invalidPhoto
                            temp[1] = true
                            setInvalidPhoto(temp)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const { photoUrl, photoKey } = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            'DEFAULT_IMAGE_HERO',
                            false,
                          )

                          if (photoUrl && photoKey && file.name) {
                            setValue('heroImage', photoKey as string, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                            setDefaultImagesState((s) => ({
                              ...s,
                              heroImage: photoUrl,
                              heroImageName: file.name,
                            }))
                          } else {
                            const temp = invalidPhoto
                            temp[1] = true
                            setInvalidPhoto(temp)
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  isUploading={isUploading}
                  imageName={defaultImagesState.heroImageName}
                  url={defaultImagesState.heroImage}
                  onRemove={() =>
                    setDefaultImagesState((s) => ({
                      ...s,
                      heroImage: '',
                      heroImageName: '',
                    }))
                  }
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                  label="Default Hero Image (1600X900)"
                  previewLabel="Default Hero Image (1600X900)"
                />
                {isErrorRatio && invalidPhoto[1] && <AlertRatio />}
              </div>
              <div>
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      accept="image/*"
                      disabled={!hasEditPermission}
                      isInvalid={
                        !!errors?.artistImage?.message || invalidPhoto[2]
                      }
                      name="artistImage"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          if (file.size > 1_000_000) {
                            const temp = invalidPhoto
                            temp[2] = true
                            setInvalidPhoto(temp)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const { photoUrl, photoKey } = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            'DEFAULT_IMAGE_ARTIST',
                            false,
                          )

                          if (photoUrl && photoKey && file.name) {
                            setValue('artistImage', photoKey as string, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                            setDefaultImagesState((s) => ({
                              ...s,
                              artistImage: photoUrl,
                              artistImageName: file.name,
                            }))
                          } else {
                            const temp = invalidPhoto
                            temp[2] = true
                            setInvalidPhoto(temp)
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  isUploading={isUploading}
                  imageName={defaultImagesState.artistImageName}
                  url={defaultImagesState.artistImage}
                  onRemove={() =>
                    setDefaultImagesState((s) => ({
                      ...s,
                      artistImage: '',
                      artistImageName: '',
                    }))
                  }
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                  label="Default Artist Image (335X335)"
                  previewLabel="Default Artist Image (335X335)"
                />
                {isErrorRatio && invalidPhoto[2] && <AlertRatio />}
              </div>
              <div>
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      accept="image/*"
                      disabled={!hasEditPermission}
                      isInvalid={
                        !!errors?.newsImage?.message || invalidPhoto[3]
                      }
                      name="newsImage"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          if (file.size > 1_000_000) {
                            const temp = invalidPhoto
                            temp[3] = true
                            setInvalidPhoto(temp)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const { photoUrl, photoKey } = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            'DEFAULT_IMAGE_NEWS',
                            false,
                          )

                          if (photoUrl && photoKey && file.name) {
                            setValue('newsImage', photoKey as string, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                            setDefaultImagesState((s) => ({
                              ...s,
                              newsImage: photoUrl,
                              newsImageName: file.name,
                            }))
                          } else {
                            const temp = invalidPhoto
                            temp[3] = true
                            setInvalidPhoto(temp)
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  isUploading={isUploading}
                  imageName={defaultImagesState.newsImageName}
                  url={defaultImagesState.newsImage}
                  onRemove={() =>
                    setDefaultImagesState((s) => ({
                      ...s,
                      newsImage: '',
                      newsImageName: '',
                    }))
                  }
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                  label="Default News Image (1020X420)"
                  previewLabel="Default News Image (1020X420)"
                />
                {isErrorRatio && invalidPhoto[3] && <AlertRatio />}
              </div>
              <div>
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      accept="image/*"
                      disabled={!hasEditPermission}
                      isInvalid={
                        !!errors?.videoGalleryImage?.message || invalidPhoto[4]
                      }
                      name="videoGalleryImage"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          if (file.size > 1_000_000) {
                            const temp = invalidPhoto
                            temp[4] = true
                            setInvalidPhoto(temp)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const { photoUrl, photoKey } = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            'DEFAULT_IMAGE_VIDEO_GALLERY',
                            false,
                          )

                          if (photoUrl && photoKey && file.name) {
                            setValue('videoGalleryImage', photoKey as string, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                            setDefaultImagesState((s) => ({
                              ...s,
                              videoGalleryImage: photoUrl,
                              videoGalleryImageName: file.name,
                            }))
                          } else {
                            const temp = invalidPhoto
                            temp[4] = true
                            setInvalidPhoto(temp)
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  isUploading={isUploading}
                  imageName={defaultImagesState.videoGalleryImageName}
                  url={defaultImagesState.videoGalleryImage}
                  onRemove={() =>
                    setDefaultImagesState((s) => ({
                      ...s,
                      videoGalleryImage: '',
                      videoGalleryImageName: '',
                    }))
                  }
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                  label="Default Video Gallery Image (870X670)"
                  previewLabel="Default Video Gallery Image (870X670)"
                />
                {isErrorRatio && invalidPhoto[4] && <AlertRatio />}
              </div>
              <div>
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      disabled={!hasEditPermission}
                      accept="image/*"
                      isInvalid={
                        !!errors?.infoImage?.message || invalidPhoto[5]
                      }
                      name="infoImage"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          if (file.size > 1_000_000) {
                            const temp = invalidPhoto
                            temp[5] = true
                            setInvalidPhoto(temp)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const { photoUrl, photoKey } = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            'DEFAULT_IMAGE_INFO',
                            false,
                          )

                          if (photoUrl && photoKey && file.name) {
                            setValue('infoImage', photoKey as string, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                            setDefaultImagesState((s) => ({
                              ...s,
                              infoImage: photoUrl,
                              infoImageName: file.name,
                            }))
                          } else {
                            const temp = invalidPhoto
                            temp[5] = true
                            setInvalidPhoto(temp)
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  isUploading={isUploading}
                  imageName={defaultImagesState.infoImageName}
                  url={defaultImagesState.infoImage}
                  onRemove={() =>
                    setDefaultImagesState((s) => ({
                      ...s,
                      infoImage: '',
                      infoImageName: '',
                    }))
                  }
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                  label="Default Info Image (890X330)"
                  previewLabel="Default Info Image (890X330)"
                />
                {isErrorRatio && invalidPhoto[5] && <AlertRatio />}
              </div>
              <div>
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      disabled={!hasEditPermission}
                      accept="image/*"
                      isInvalid={
                        !!errors?.stagesLogo?.message || invalidPhoto[6]
                      }
                      name="stagesLogo"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          if (file.size > 1_000_000) {
                            const temp = invalidPhoto
                            temp[6] = true
                            setInvalidPhoto(temp)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const { photoUrl, photoKey } = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            'DEFAULT_IMAGE_STAGES_LOGO',
                            false,
                          )

                          if (photoUrl && photoKey && file.name) {
                            setValue('stagesLogo', photoKey as string, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                            setDefaultImagesState((s) => ({
                              ...s,
                              stagesLogo: photoUrl,
                              stagesLogoName: file.name,
                            }))
                          } else {
                            const temp = invalidPhoto
                            temp[6] = true
                            setInvalidPhoto(temp)
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  isUploading={isUploading}
                  imageName={defaultImagesState.stagesLogoName}
                  url={defaultImagesState.stagesLogo}
                  onRemove={() =>
                    setDefaultImagesState((s) => ({
                      ...s,
                      stagesLogo: '',
                      stagesLogoName: '',
                    }))
                  }
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                  label="Default Logo for Stages (32X32)"
                  previewLabel="Default Logo for Stages (32X32)"
                />
                {isErrorRatio && invalidPhoto[6] && <AlertRatio />}
              </div>
              <div>
                <FormUploadPreview
                  control={
                    <Input
                      multiple={false}
                      accept="image/*"
                      disabled={!hasEditPermission}
                      isInvalid={
                        !!errors?.checkinImage?.message || invalidPhoto[7]
                      }
                      name="checkinImage"
                      onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                        const file = e?.target?.files?.[0]
                        if (file) {
                          if (file.size > 1_000_000) {
                            const temp = invalidPhoto
                            temp[7] = true
                            setInvalidPhoto(temp)
                            setIsError(true)
                            setShowMessageBox(true)
                            setMessage('The photo is too large (1 MB)')
                            return
                          }
                          const { photoUrl, photoKey } = await uploadImage(
                            file,
                            setIsUploading,
                            setIsError,
                            setShowMessageBox,
                            setMessage,
                            setIsErrorRatio,
                            'DEFAULT_IMAGE_CHECK_IN',
                            false,
                          )

                          if (photoUrl && photoKey && file.name) {
                            setValue('checkinImage', photoKey as string, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                            setDefaultImagesState((s) => ({
                              ...s,
                              checkinImage: photoUrl,
                              checkinImageName: file.name,
                            }))
                          } else {
                            const temp = invalidPhoto
                            temp[7] = true
                            setInvalidPhoto(temp)
                          }
                        }
                      }}
                      type="file"
                    />
                  }
                  isUploading={isUploading}
                  imageName={defaultImagesState.checkinImageName}
                  url={defaultImagesState.checkinImage}
                  onRemove={() =>
                    setDefaultImagesState((s) => ({
                      ...s,
                      checkinImage: '',
                      checkinImageName: '',
                    }))
                  }
                  imageStyle={{
                    objectFit: 'contain',
                  }}
                  label="Default Check-in Image (110X110)"
                  previewLabel="Default Check-in Image (110X110)"
                />
                {isErrorRatio && invalidPhoto[7] && <AlertRatio />}
              </div>
            </div>
          </form>

          <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120 mt-4">
            <Button
              onClick={() => navigate(-1)}
              variant="secondary"
              data-dismiss="modal"
              disabled={!hasEditPermission}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="settings-default-images-form"
              type="submit"
              disabled={isUploading || !hasEditPermission}
            >
              Save
            </Button>
          </div>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
